import { t } from '@lingui/core/macro';
import moment from 'moment';

import { isOverlappingTime } from '@rover/components/src/datetime/TimePickerDay/getOverlappingTimes';
import { getTimeFormat } from '@rover/react-lib/src/factories/timeFactory';
import { isSameDay, isTimeInThePastForToday } from '@rover/react-lib/src/utils/datetime';
import { isBeforeTime } from '@rover/react-lib/src/utils/time';
import type { FuzzyTime } from '@rover/types/src/datetime/FuzzyTime';
import { FormError, FormValidator, FormValidatorCreator } from '@rover/types/src/Form';
import { getWordCount } from '@rover/utilities/wordCount';

export const required: FormValidatorCreator = (message?: string) => (prop: unknown) => {
  const m = message || t`This field is required.`;
  if (prop === 0 || prop === false) return null;
  if (typeof prop === 'string' && !prop.trim()) return m;
  return prop ? null : m;
};

export const truthy: FormValidatorCreator = (message?: string) => (prop) => {
  const m = message || t`This field must be truthy.`;
  return prop ? null : m;
};

export const agreeToTos: FormValidatorCreator = (message?: string) => (prop) => {
  const m = message || t`Please agree to our Terms of Service and Privacy Statement.`;
  return prop ? null : m;
};

export const minValue: FormValidatorCreator = (value: number, message?: string) => (prop) => {
  const m = message || t`This field must be at least ${value}.`;
  if (!prop && prop !== 0) return m;
  return Number(prop) >= value ? null : m;
};

export const minDate: FormValidatorCreator = (value: Date, message?: string) => (prop: Date) => {
  const m = message || t`This field must be at least ${String(value)}.`;
  return prop >= value ? null : m;
};

export const maxDate: FormValidatorCreator = (value: Date, message?: string) => (prop: Date) => {
  const m = message || t`This field must be no later than ${String(value)}.`;
  return prop < value ? null : m;
};

export const maxValue: FormValidatorCreator = (value: number, message?: string) => (prop) => {
  const m = message || t`This field must be no more than ${value}.`;
  if (!prop && prop !== 0) return m;
  return Number(prop) <= value ? null : m;
};

export const minLength: FormValidatorCreator =
  (length = 10, message?: string) =>
  (prop: any) => {
    const m = message || t`This field must be at least ${length} characters long.`;
    if (!prop) return m;
    return prop.length >= length ? null : m;
  };

export const maxLength: FormValidatorCreator = (length: number, message?: string) => (prop) => {
  const m = message || t`This field must be at most ${length} characters long.`;
  if (typeof prop !== 'string') return m;
  return prop.length <= length ? null : m;
};

export const withinInclusiveRange: FormValidatorCreator =
  (min: number, max: number, message?: string) => (prop) => {
    const m = message || t`This field must be between ${min} and ${max}.`;
    if (typeof prop !== 'number') return m;
    const meetsMin = min <= prop;
    const meetsMax = max >= prop;
    return meetsMin && meetsMax ? null : m;
  };

export const minWordCount: FormValidatorCreator = (length: number, message?: string) => (prop) => {
  const m = message || t`Please write at least ${length} words.`;
  if (typeof prop !== 'string') return m;
  const wordCount = getWordCount(prop);
  const meetsWordCount = typeof wordCount === 'number' && wordCount >= length;
  return meetsWordCount ? null : m;
};

export const zipcode: FormValidatorCreator =
  (regex?: RegExp | null | undefined, message?: string) => (prop) => {
    const m = message || t`Enter a valid zipcode.`;
    if (!prop || typeof prop !== 'string') return m;
    const zipcodeRegex =
      regex || /(^\d{5}(-\d{4})?$)|(^[ABCEGHJKLMNPRSTVXY]{1}\d{1}[A-Z]{1} *\d{1}[A-Z]{1}\d{1}$)/;
    return zipcodeRegex.test(prop.trim()) ? null : m;
  };

/* https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/email */
const emailTestRegexp = new RegExp(
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/
);
export const email: FormValidatorCreator =
  (message?: string): FormValidator =>
  (prop: unknown): FormError => {
    const m = message || t`Please enter a valid email address`;
    return typeof prop === 'string' && emailTestRegexp.test(prop) ? null : m;
  };

export const notEmptyArray: FormValidatorCreator = (message?: string) => (prop) => {
  const m = message || t`This field is required.`;
  return Array.isArray(prop) && prop.length > 0 ? null : m;
};

export const cardHolderName: FormValidatorCreator = (message?: string) => (prop) => {
  const m = message || t`We cannot store names with only numbers in our system.`;
  return typeof prop === 'string' && /^[\d\s-]+$/.test(prop) ? m : null;
};

export const noUndefinedTimes: FormValidatorCreator = (message?: string) => (days) => {
  const m = message || t`Please select a time.`;
  if (!Array.isArray(days)) return [[m]];
  const errors: Array<Array<string | null>> = [];
  let isError = false;
  days.forEach((day) => {
    const dayErrors: Array<string | null> = [];
    day.times.forEach((time) => {
      if (!time.value) {
        isError = true;
        dayErrors.push(m);
      } else {
        dayErrors.push(null);
      }
    });
    errors.push(dayErrors);
  });
  return isError ? errors : null;
};

export const noPastDaysTimesForToday: FormValidatorCreator = (message?: string) => (days) => {
  const m = message || t`Time cannot be in the past`;
  if (!Array.isArray(days)) return null;
  const errors: Array<Array<string | null>> = [];
  let isError = false;
  days.forEach((day) => {
    const dayErrors: Array<string | null> = [];
    day.times.forEach((time) => {
      if (isTimeInThePastForToday(day.date, time?.value)) {
        isError = true;
        dayErrors.push(m);
      } else {
        dayErrors.push(null);
      }
    });
    errors.push(dayErrors);
  });
  return isError ? errors : null;
};

export const someDaysWithTimes: FormValidatorCreator = (message?: string) => (days) => {
  const m = message || t`Please add at least one day with times`;
  if (!Array.isArray(days)) return m;
  const daysWithTimes = days.some((day) => day.times && day.times.length > 0);
  return daysWithTimes ? null : m;
};

export const maxNumberOfTimesPerDay: FormValidatorCreator =
  (value: number, message?: string) => (days) => {
    const m = message || t`You can only schedule up to ${value} times per day.`;
    if (!Array.isArray(days)) return [[m]];
    const errors: Array<Array<string | null>> = [];
    let isError = false;
    days.forEach((day) => {
      const dayErrors: Array<string | null> = [];

      if (Array.isArray(day.times) && day.times.length > value) {
        isError = true;
        dayErrors.push(m);
      } else {
        dayErrors.push(null);
      }

      errors.push(dayErrors);
    });
    return isError ? errors : null;
  };

export const overlappingOfTimesPerDay: FormValidatorCreator =
  (timeSelectionDuration: number, message?: string) => (days) => {
    const m = message || t`Time ranges cannot overlap.`;
    if (!Array.isArray(days)) return [[m]];
    const errors: Array<Array<string | null>> = [];
    let isError = false;
    days.forEach((day) => {
      const dayErrors: Array<string | null> = [];
      day.times.forEach((_time, idx) => {
        if (isOverlappingTime(idx, day.times, timeSelectionDuration)) {
          isError = true;
          dayErrors.push(m);
        } else {
          dayErrors.push(null);
        }
      });
      errors.push(dayErrors);
    });
    return isError ? errors : null;
  };

export const requiredFuzzyTime: FormValidatorCreator = (message?: string) => (fuzzyTime) => {
  const m = message || t`Please select a start and end time.`;
  return fuzzyTime &&
    typeof fuzzyTime === 'object' &&
    fuzzyTime.earliest &&
    fuzzyTime.earliest.value &&
    fuzzyTime.latest &&
    fuzzyTime.latest.value
    ? null
    : m;
};

export const requiredPeriod: FormValidatorCreator =
  (
    pickUpDate: { date: Date | null; time: FuzzyTime | Partial<FuzzyTime> },
    dropOffDate: { date: Date | null; time: FuzzyTime | Partial<FuzzyTime> },
    message?: string
  ) =>
  () => {
    const m = message || t`Select a start and end date`;
    return pickUpDate &&
      dropOffDate &&
      pickUpDate?.date !== null &&
      typeof pickUpDate?.date !== 'undefined' &&
      dropOffDate?.date !== null &&
      typeof dropOffDate?.date !== 'undefined'
      ? null
      : m;
  };

export const requiredStartTime: FormValidatorCreator = (message?: string) => (time) => {
  const m = message || t`Select a start time`;
  return time && typeof time === 'object' && time.earliest && time.earliest.value ? null : m;
};

export const requiredEndTime: FormValidatorCreator = (message?: string) => (time) => {
  const m = message || t`Select an end time`;
  return time && typeof time === 'object' && time.earliest && time.earliest.value ? null : m;
};

export const noPastTimeForToday: FormValidatorCreator =
  (date: { date: Date }, message?: string) => (time) => {
    const m = message || t`Time cannot be in the past`;
    if (isTimeInThePastForToday(date.date, time?.earliest?.value)) {
      return m;
    }
    return null;
  };

export const fuzzyTimeRangeValidator: FormValidatorCreator =
  (message?: string) => (pudo: { date?: Date; time: FuzzyTime | Partial<FuzzyTime> }) => {
    const m = message || t`The start time must be before the end time`;
    if (
      pudo.time.earliest &&
      pudo.time.latest &&
      isBeforeTime(pudo.time.latest, pudo.time.earliest)
    ) {
      return m;
    }
    return null;
  };

export const requiredDropOffBeforePickup: FormValidatorCreator =
  (
    pickUp: { date: Date; time: FuzzyTime | Partial<FuzzyTime> },
    dropOff: { date: Date; time: FuzzyTime | Partial<FuzzyTime> },
    message?: string,
    validateOnlyWhenDatesSelected: boolean = false
  ) =>
  () => {
    const m = message || t`Must be after start time`;
    if (validateOnlyWhenDatesSelected) {
      if (!pickUp?.date || !dropOff?.date) {
        return null;
      }
    }
    const pickUpDate = pickUp?.date ?? moment().toDate();
    const dropOffDate = dropOff?.date ?? moment().toDate();
    if (isSameDay(pickUpDate, dropOffDate)) {
      if (dropOff?.time?.earliest?.value && pickUp?.time?.earliest?.value) {
        if (
          moment(dropOff.time.earliest.value, getTimeFormat()).isSameOrAfter(
            moment(pickUp.time.earliest.value, getTimeFormat())
          )
        ) {
          return m;
        }
      }
    }
    return null;
  };

export const fieldsMatch: FormValidatorCreator =
  (matchFieldValue: any, message?: string) => (prop: any) => {
    const m = message || t`Fields must match.`;
    return prop === matchFieldValue ? null : m;
  };

export const noSpecialCharactersOnfidoNames: FormValidatorCreator =
  (message?: string) => (prop) => {
    const m = message || t`Special characters are not allowed`;
    return /^[\p{L}\-—–,.''‘’ ]+$/u.test(prop) ? null : m;
  };

export const noNumbers: FormValidatorCreator = (message?: string) => (prop) => {
  const m = message || t`Numbers are not allowed`;
  return /^([^0-9]*)$/.test(prop) ? null : m;
};

export const noDoubleWhiteSpaces: FormValidatorCreator = (message?: string) => (prop) => {
  const m = message || t`Duplicate whitespaces are not allowed`;
  return /\s{2,}/g.test(prop) ? m : null;
};
